import NiceModal from '@ebay/nice-modal-react';
import { MatrixClient } from 'matrix-js-sdk/src';
import { useContext } from 'react';

import useAuthContext from 'contexts/AuthProvider/useAuthContext';
import useToast from 'contexts/ToastProvider/useToast';
import { storageUtils } from 'modules/CalendarView/CalendarSync/CalendarSyncModal/utils';
import { clearSearchIndex, deleteSearchIndexDatabase } from 'modules/Messenger/Search/SearchIndexStore';
import { NotificationsContext } from 'modules/Notifications/NotificationsProvider/NotificationsProvider';
import logger from 'utils/logging/faro';
import { authLogger, storesLogger } from 'utils/logging/scopedLogger';
import { clearAllIndexedDB, CustomMeedioMatrixEvents } from 'utils/matrixUtils';

import { teardownSessionOverlays } from './sessionOverlayTeardown';
import useMxCookies from './useMxCookies';
import { emitSessionLifecycleEvent } from '../sessionObservability';

/**
 * Use logout hook for logging out of the matrix account
 * Clears the matrix session, indexDB, cookies and the matrix client
 * Reloads the page after logging out
 * @returns logout function
 */
const useLogout = () => {
  const { clearCookies } = useMxCookies();
  const { setAuthPageVisited } = useAuthContext();
  const { clearAllToasts } = useToast();
  const notificationsContext = useContext(NotificationsContext);
  const niceModalContext = useContext(NiceModal.NiceModalContext);

  const logout = async (matrixClient: MatrixClient, reload = false) => {
    teardownSessionOverlays({
      clearAllToasts,
      clearAllNotifications: notificationsContext?.clearAllNotifications,
      niceModalContext,
    });

    matrixClient
      .setAccountData(CustomMeedioMatrixEvents.CALENDAR, {
        calendars: [],
      } as never)
      .catch(() => logger.error('Failed to set calendar account data after account logout'));

    authLogger.info('Logout started');
    emitSessionLifecycleEvent({
      domain: 'logout',
      action: 'logout_start',
      reload,
      mx_device_id: matrixClient.getDeviceId?.() ?? undefined,
    });
    setAuthPageVisited(false);

    // Wipe any encrypted search index for this user BEFORE the matrix client
    // is stopped so we don't race with a background persist write and leave
    // stale per-user rows behind on account switch.
    const userIdForSearch = matrixClient.getUserId();
    if (userIdForSearch) {
      await clearSearchIndex(userIdForSearch).catch((e) =>
        storesLogger.error('failed to clear the search index on logout', e)
      );
    }

    authLogger.info('Clearing cookies');
    clearCookies();

    storageUtils.removeTokens();

    authLogger.info('Logging out of the matrixClient and stopping the client');
    await matrixClient
      .logout(true)
      .catch((e) => authLogger.warn('Matrix logout request failed — homeserver may be unreachable', e));

    storesLogger.warn('clearing the matrix stores in logout');
    await matrixClient
      .clearStores()
      .catch((e) => storesLogger.error('failed to clear the stores in logout', e))
      .then(() => storesLogger.info('successfully cleared the matrix stores in logout'));

    // Belt-and-suspenders clear sequence for the encrypted search index.
    //
    // 1. `deleteSearchIndexDatabase()` by exact name. This is the only
    //    strategy that works on browsers where `indexedDB.databases()` is
    //    missing (Firefox < 126, some mobile WebViews) — in those environments
    //    `clearAllIndexedDB` below silently becomes a no-op and would leave
    //    the search DB intact across logout + re-login.
    // 2. `clearAllIndexedDB()` — best-effort global wipe for everything else
    //    (matrix-js-sdk crypto store, etc.).
    // 3. A final `deleteSearchIndexDatabase()` guard. The persist timer in
    //    `useRoomSearch` runs on a 2 s debounce; if it fires between steps 1
    //    and 2 and recreates the DB, this last call removes it again.
    //    Using `deleteSearchIndexDatabase` (not `clearSearchIndex`) is
    //    critical — `clearSearchIndex` calls `indexedDB.open()` which would
    //    recreate an empty DB shell, causing `chat-s-index` to appear in
    //    DevTools after every logout.
    authLogger.info('Deleting search index DB by name');
    await deleteSearchIndexDatabase();

    authLogger.info('Clearing all IndexedDB');
    await clearAllIndexedDB();

    authLogger.info('Final search index DB delete (debounce-race guard)');
    await deleteSearchIndexDatabase();
    authLogger.info('Logout completed');
    emitSessionLifecycleEvent({
      domain: 'logout',
      action: 'logout_complete',
      reload,
      mx_device_id: matrixClient.getDeviceId?.() ?? undefined,
    });

    if (reload) {
      authLogger.info('Reloading page after logout, because reload argument is true');
      window.location.reload();
    }
  };

  return logout;
};

export default useLogout;
